class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 800);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static async renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.requestController?.abort();
    const controller = new AbortController();
    FacetFiltersForm.requestController = controller;
    const params = new URLSearchParams(searchParams);
    const preview = new URLSearchParams(location.search).get('preview_theme_id');
    if (preview) params.set('preview_theme_id', preview);
    searchParams = params.toString();
    const order = params.get('sort_by');
    const sizeSort = order === 'size-ascending' || order === 'size-descending';
    const requestedPage = Math.max(1, Number(params.get('page')) || 1);
    const gridContainer = document.getElementById('ProductGridContainer');
    const errorNode = document.getElementById('VeyeaSortError');
    errorNode?.remove();
    gridContainer.setAttribute('aria-busy', 'true');
    gridContainer.querySelector('.collection')?.classList.add('loading');
    document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(node => node.classList.remove('hidden'));
    const updateEvent = FacetFiltersForm.startUpdateEvent(searchParams);
    const parser = new DOMParser();
    const section = FacetFiltersForm.getSections()[0].section;
    const serverParams = new URLSearchParams(params);
    serverParams.set('section_id', section);
    if (sizeSort) serverParams.set('sort_by', 'title-ascending');
    const fetchDocument = async (page) => {
      const query = new URLSearchParams(serverParams);
      if (page !== null) query.set('page', String(page));
      const url = `${location.pathname}?${query}`;
      const cached = FacetFiltersForm.filterData.find(item => item.url === url);
      let html = cached?.html;
      if (!html) {
        const response = await fetch(url, {signal: controller.signal});
        if (!response.ok) throw new Error(`Collection response ${response.status}`);
        html = await response.text();
        FacetFiltersForm.filterData.push({url, html});
        if (FacetFiltersForm.filterData.length > 30) FacetFiltersForm.filterData.shift();
      }
      const doc = parser.parseFromString(html, 'text/html');
      if (!doc.getElementById('ProductGridContainer')) throw new Error('Collection section missing');
      return doc;
    };
    try {
      let result = await fetchDocument(1);
      if (result.getElementById('ProductGridContainer').dataset.vyVariants === 'true') {
      const firstContainer = result.getElementById('ProductGridContainer');
      const pages = Math.max(1, Number(firstContainer.dataset.vyPages) || 1);
      const perPage = Math.max(1, Number(firstContainer.dataset.vyPerPage) || 36);
      const documents = [result];
      for (let page = 2; page <= pages; page++) documents.push(await fetchDocument(page));
      const uniqueCards = new Map();
      documents.forEach(doc => doc.querySelectorAll('#product-grid > li').forEach(card => {
        const id = card.querySelector('[data-vy-variant-id]')?.dataset.vyVariantId;
        if (id && !uniqueCards.has(id)) uniqueCards.set(id, card);
      }));
      const cards = Array.from(uniqueCards.values());
      if (sizeSort || order === 'price-ascending' || order === 'price-descending') {
        const metric = card => {
          const node = card.querySelector('[data-vy-variant-id]');
          const raw = sizeSort ? node?.dataset.vySize : node?.dataset.vyPrice;
          const value = raw ? Number(raw) : NaN;
          return Number.isFinite(value) && (!sizeSort || value > 0) ? value : null;
        };
        cards.sort((left, right) => {
          const a = metric(left), b = metric(right);
          if (a === null) return b === null ? 0 : 1;
          if (b === null) return -1;
          return order.endsWith('ascending') ? a - b : b - a;
        });
      }
      const variationPages = Math.max(1, Math.ceil(cards.length / perPage));
      const currentPage = Math.min(requestedPage, variationPages);
      if (currentPage !== requestedPage) {
        params.set('page', String(currentPage));
        searchParams = params.toString();
      }
      const grid = result.querySelector('ul#product-grid');
      if (grid) grid.replaceChildren(...cards.slice((currentPage - 1) * perPage, currentPage * perPage));
      result.querySelectorAll('.pagination-wrapper').forEach(node => node.remove());
      if (grid && variationPages > 1) {
        const wrapper = result.createElement('div');
        wrapper.className = 'pagination-wrapper';
        const nav = result.createElement('nav');
        nav.className = 'pagination'; nav.setAttribute('aria-label', 'Pagination');
        const list = result.createElement('ul');
        list.className = 'pagination__list list-unstyled';
        for (let page = 1; page <= variationPages; page++) {
          const li = result.createElement('li');
          const link = result.createElement('a');
          const query = new URLSearchParams(params);
          query.set('page', String(page)); query.delete('section_id');
          link.href = location.pathname + '?' + query;
          link.className = 'pagination__item link';
          link.dataset.vyVariantPage = String(page);
          link.textContent = String(page);
          if (page === currentPage) {
            link.setAttribute('aria-current', 'page');
            link.classList.add('pagination__item--current');
          }
          li.append(link); list.append(li);
        }
        nav.append(list); wrapper.append(nav); grid.after(wrapper);
      }
      const countText = cards.length + ' ' + (firstContainer.dataset.vyCountLabel || 'variations');
      result.querySelectorAll('#ProductCount, #ProductCountDesktop, .mobile-facets__count').forEach(node => {
        node.textContent = countText;
        node.dataset.productCount = String(cards.length);
      });
      } else {
      result = await fetchDocument(sizeSort ? 1 : null);
      if (sizeSort) {
        const firstContainer = result.getElementById('ProductGridContainer');
        const pages = Math.max(1, Number(firstContainer.dataset.vyPages) || 1);
        const perPage = Number(firstContainer.dataset.vyPerPage);
        const documents = [result];
        for (let page = 2; page <= pages; page++) documents.push(await fetchDocument(page));
        const cards = documents.flatMap(doc => Array.from(doc.querySelectorAll('#product-grid > li')));
        const getSize = card => {
          const value = card.querySelector('[data-vy-size]')?.dataset.vySize;
          const size = value ? Number(value) : NaN;
          return Number.isFinite(size) && size > 0 ? size : null;
        };
        cards.sort((left, right) => {
          const a = getSize(left), b = getSize(right);
          if (a === null) return b === null ? 0 : 1;
          if (b === null) return -1;
          return order === 'size-ascending' ? a - b : b - a;
        });
        const currentPage = Math.min(requestedPage, pages);
        result = documents[currentPage - 1];
        const grid = result.querySelector('ul#product-grid');
        if (grid) grid.replaceChildren(...cards.slice((currentPage - 1) * perPage, currentPage * perPage));
        result.querySelectorAll('a[href]').forEach(link => {
          if (!link.closest('.pagination-wrapper, facet-remove')) return;
          const url = new URL(link.getAttribute('href'), location.origin);
          url.searchParams.set('sort_by', order);
          if (preview) url.searchParams.set('preview_theme_id', preview);
          url.searchParams.delete('section_id');
          link.setAttribute('href', url.pathname + url.search + url.hash);
        });
      }
      }
      if (controller.signal.aborted) return;
      FacetFiltersForm.searchParamsPrev = searchParams;
      FacetFiltersForm.renderSection(result.documentElement.outerHTML, event, updateEvent);
      document.querySelectorAll('.js-filter > summary').forEach(summary => {
        const details = summary.parentElement;
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', String(details.open));
        if (!details.dataset.vyToggleBound) {
          details.dataset.vyToggleBound = 'true';
          details.addEventListener('toggle', () => details.querySelector('summary')?.setAttribute('aria-expanded', String(details.open)));
        }
      });
      document.querySelectorAll('select[name="sort_by"]').forEach(select => {
        if (Array.from(select.options).some(option => option.value === order)) select.value = order;
      });
      document.querySelectorAll('[data-vy-sort]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.vySort === order)));
      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    } catch (error) {
      if (error.name !== 'AbortError') {
        const message = document.createElement('p');
        message.id = 'VeyeaSortError';
        message.setAttribute('role', 'alert');
        message.textContent = gridContainer.dataset.vySortError || 'Please try again.';
        gridContainer.prepend(message);
        updateEvent?.reject(error);
      }
    } finally {
      if (FacetFiltersForm.requestController === controller) {
        gridContainer.removeAttribute('aria-busy');
        gridContainer.querySelector('.collection')?.classList.remove('loading');
        document.querySelectorAll('.facets-container .loading__spinner, facet-filters-form .loading__spinner').forEach(node => node.classList.add('hidden'));
        FacetFiltersForm.toggleActiveFacets(false);
      }
    }
  }

  static startUpdateEvent(searchParams) {
    const { SearchUpdateEvent, CollectionUpdateEvent } = window.StandardEvents || {};
    if (!SearchUpdateEvent && !CollectionUpdateEvent) return;

    const facetsForm = document.querySelector('facet-filters-form');
    const facetsContainer = document.querySelector('.facets-container');
    const collectionId = facetsContainer?.dataset.collectionId || null;
    const collectionHandle = facetsContainer?.dataset.collectionHandle || '';
    const currentCount = parseInt(document.getElementById('ProductCount')?.dataset.productCount) || 0;
    const urlSearchParams = new URLSearchParams(searchParams);
    const isSearchPage = facetsContainer?.dataset.template === 'search';
    const isCollectionPage = facetsContainer?.dataset.template === 'collection';
    const dispatchTarget = facetsForm || document;

    let deferred;

    if (isSearchPage) {
      deferred = SearchUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new SearchUpdateEvent({
          search: {
            query: urlSearchParams.get('q') || '',
            productFilters: SearchUpdateEvent.parseProductFilters(urlSearchParams),
            sortKey: SearchUpdateEvent.getSortKey(urlSearchParams),
          },
          promise: deferred.promise,
        }),
      );
    } else if (isCollectionPage) {
      // collectionId is null for auto-generated collections (/collections/all,
      // /collections/vendors/<x>, etc.) — the SSE schema accepts that.
      deferred = CollectionUpdateEvent.createPromise();
      dispatchTarget.dispatchEvent(
        new CollectionUpdateEvent({
          collection: { id: collectionId, handle: collectionHandle, productsCount: currentCount },
          productFilters: CollectionUpdateEvent.parseProductFilters(urlSearchParams),
          sortKey: CollectionUpdateEvent.getSortKey(urlSearchParams),
          promise: deferred.promise,
        }),
      );
    }

    if (!deferred) return;

    return {
      resolve: (filteredCount) => {
        deferred.resolve({ [isSearchPage ? 'totalCount' : 'productsCount']: filteredCount });
      },
      reject: deferred.reject,
    };
  }

  static renderSectionFromFetch(url, event, updateEvent) {
    fetch(url)
      .then((response) => response.text())
      .then((html) => {
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderSection(html, event, updateEvent);
      })
      .catch((error) => {
        console.error(error);
        updateEvent?.reject(error);
      });
  }

  static renderSectionFromCache(filterDataUrl, event, updateEvent) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderSection(html, event, updateEvent);
  }

  static renderSection(html, event, updateEvent) {
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html, updateEvent);
    setTimeout(() => VeyeaColorCounts.refreshOpen(), 0);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    document.getElementById('ProductGridContainer').innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document
      .getElementById('ProductGridContainer')
      .querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });
  }

  static renderProductCount(html, updateEvent) {
    const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
    const sourceCount = parsedHtml.getElementById('ProductCount');
    const count = sourceCount.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.dataset.productCount = sourceCount.dataset.productCount || '';
    container.dataset.totalCount = sourceCount.dataset.totalCount || '';
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner',
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));

    updateEvent?.resolve(parseInt(sourceCount.dataset.productCount) || 0);
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter',
    );

    // Remove facets that are no longer returned from the server
    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      // Element already rendered in the DOM so just update the innerHTML
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          // Same facet type (eg horizontal/vertical or drawer/mobile)
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;

      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (!isTextInput) {
          // Try to return focus to the same checkbox the user just toggled,
          // re-selecting it from the freshly rendered HTML by its id.
          const originatingInputId = event.target.id;
          const matchingInput = originatingInputId
            ? newFacetDetailsElement.querySelector(`#${CSS.escape(originatingInputId)}`)
            : null;

          if (matchingInput) {
            matchingInput.focus();
          } else {
            // Fallback to summary/close button if the checkbox can't be found
            const fallbackSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
              ? `.mobile-facets__close-button`
              : `.facets__summary`;
            const fallbackElement = newFacetDetailsElement.querySelector(fallbackSelector);
            if (fallbackElement) fallbackElement.focus();
          }
        }
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();
if (document.getElementById('ProductGridContainer')?.dataset.vyVariants === 'true' || /^size-(ascending|descending)$/.test(new URLSearchParams(location.search).get('sort_by'))) {
  FacetFiltersForm.renderPage(location.search.slice(1), null, false);
}

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);


document.addEventListener('click', (event) => {
  const button = event.target.closest('[data-vy-sort]');
  if (!button) return;
  const params = new URLSearchParams(location.search);
  params.set('sort_by', button.dataset.vySort);
  params.delete('page');
  FacetFiltersForm.renderPage(params.toString());
});
document.querySelectorAll('[data-vy-sort]').forEach(button => {
  button.setAttribute('aria-pressed', String(button.dataset.vySort === new URLSearchParams(location.search).get('sort_by')));
});

document.addEventListener('click', event => {
  const link = event.target.closest('a[data-vy-variant-page]');
  if (!link || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
  event.preventDefault();
  FacetFiltersForm.renderPage(new URL(link.href).search.slice(1));
  document.getElementById('main-collection-filters')?.scrollIntoView({block: 'start'});
});

class VeyeaColorCounts {
  static cache = new Map();
  static generation = 0;
  static refreshOpen() {
    const markers = Array.from(document.querySelectorAll('[data-vy-color-count]'));
    if (!markers.some(node => node.closest('details')?.open)) return;
    const params = new URLSearchParams(location.search);
    params.delete('filter.p.m.custom.filter_colors');
    params.delete('page');
    params.delete('sort_by');
    const section = document.getElementById('product-grid')?.dataset.id;
    if (!section) return;
    params.set('section_id', section);
    const generation = ++this.generation;
    const jobs = Array.from(new Set(markers.map(node => node.dataset.vyValue)));
    const worker = async () => {
      while (jobs.length && generation === this.generation) {
        const value = jobs.shift();
        const query = new URLSearchParams(params);
        query.set('filter.p.m.custom.filter_colors', value);
        const key = location.pathname + '?' + query.toString();
        const apply = (count) => {
          if (generation !== this.generation) return;
          document.querySelectorAll('[data-vy-color-count]').forEach(node => {
            if (node.dataset.vyValue !== value) return;
            node.querySelector('[data-vy-count-number]').textContent = count === null ? '' : '(' + count + ')';
          });
        };
        try {
          if (!this.cache.has(key)) {
            const request = (async () => {
              const ids = new Set();
              let pages = 1;
              for (let page = 1; page <= pages; page++) {
                const pageQuery = new URLSearchParams(query);
                pageQuery.set('page', String(page));
                const response = await fetch(location.pathname + '?' + pageQuery, {credentials: 'same-origin'});
                if (!response.ok) throw new Error('Color count response ' + response.status);
                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const grid = doc.getElementById('ProductGridContainer');
                if (!grid || grid.dataset.vyVariants !== 'true') throw new Error('Variation grid missing');
                pages = Math.max(1, Number(grid.dataset.vyPages) || 1);
                grid.querySelectorAll('[data-vy-variant-id]').forEach(card => {
                  if (card.dataset.vyVariantId) ids.add(card.dataset.vyVariantId);
                });
              }
              return ids.size;
            })();
            this.cache.set(key, request);
          }
          apply(await this.cache.get(key));
        } catch (error) {
          this.cache.delete(key);
          apply(null);
        }
      }
    };
    for (let workerIndex = 0; workerIndex < 3; workerIndex++) worker();
  }
}
document.addEventListener('toggle', event => {
  if (event.target instanceof HTMLDetailsElement && event.target.open && event.target.querySelector('[data-vy-color-count]')) {
    VeyeaColorCounts.refreshOpen();
  }
}, true);
VeyeaColorCounts.refreshOpen();
